import { ISessionEndedReason } from '@booking-portal/modules/booking-flow-checkout/booking-flow-checkout-types';
import { useCheckoutHash } from '@booking-portal/modules/booking-flow-checkout/composables/use-checkout-hash';
import { routesFactoryBookingFlow } from '@booking-portal/modules/booking-flow/booking-flow-routes';
import type { IAppRoutes } from '@booking-portal/router';
import { guardNames } from '@booking-portal/router/router-constants';
import type { IBasketFlowStepId } from '@booking-portal/types/basket-flow-types';
import { parseToInteger } from '@booking-portal/utils/format-utils';
import type { RouteLocationGeneric } from 'vue-router';

export const bookingFlowCheckoutNames = {
  startCheckout: 'booking-flow-checkout-start',
  continueCheckout: 'booking-flow-checkout-continue-checkout',
  esignatureWebhook: 'booking-flow-checkout-esignature-webhook',
  idCheckWebhook: 'booking-flow-checkout-id-check-webhook',
  checkout: 'booking-flow-checkout-root',

  payment: 'booking-flow-checkout-step-payment',
  paymentVerify: 'booking-flow-checkout-step-payment-verify',

  eSignature: 'booking-flow-checkout-e-signature',
  eSignatureVerify: 'booking-flow-checkout-e-signature-verify',
  eSignatureSignedWebhook: 'booking-flow-checkout-e-signature-signed',
  eSignatureCancelled: 'booking-flow-checkout-e-signature-cancelled',
  eSignatureExpired: 'booking-flow-checkout-e-signature-expired',
  eSignatureError: 'booking-flow-checkout-e-signature-error',

  idCheck: 'booking-flow-checkout-step-id-check',
  idCheckInProgress: 'booking-flow-checkout-step-id-check-in-progress',
  idCheckCancelled: 'booking-flow-checkout-step-id-check-cancelled',
  idCheckExpired: 'booking-flow-checkout-step-id-check-expired',
  idCheckNotAllowed: 'booking-flow-checkout-step-id-check-not-allowed',
  idCheckSetupError: 'booking-flow-checkout-setup-error',

  createBooking: 'booking-flow-checkout-create-booking',
  sessionEnded: 'booking-flow-checkout-session-ended',
  success: 'booking-flow-checkout-success',
};

/**
 * Serverside step-name to local-route-name map.
 */
export const bookingFlowCheckoutRouteStepLookup: Record<
  IBasketFlowStepId,
  string
> = {
  payment: bookingFlowCheckoutNames.payment,
  'id-check': bookingFlowCheckoutNames.idCheck,
  'id-check-in-progress': bookingFlowCheckoutNames.idCheckInProgress,
  'id-check-cancelled': bookingFlowCheckoutNames.idCheckCancelled,
  'id-check-expired': bookingFlowCheckoutNames.idCheckExpired,
  'id-check-not-allowed': bookingFlowCheckoutNames.idCheckNotAllowed,
  'e-signature': bookingFlowCheckoutNames.eSignature,
  'e-signature-expired': bookingFlowCheckoutNames.eSignatureExpired,
  'e-signature-cancelled': bookingFlowCheckoutNames.eSignatureCancelled,
  'create-booking': bookingFlowCheckoutNames.createBooking,
  unknown: bookingFlowCheckoutNames.continueCheckout,
};

/**
 * The home page of steps. E.g. the home page of the `id-check-*` steps is the `id-check` page. The home page of 'payment-verify' is 'payment'.
 */
export const bookingFlowCheckoutRouteStepHomePageLookup: Record<
  IBasketFlowStepId,
  string
> = {
  payment: bookingFlowCheckoutNames.payment,
  'id-check': bookingFlowCheckoutNames.idCheck,
  'id-check-in-progress': bookingFlowCheckoutNames.idCheckInProgress,
  'id-check-cancelled': bookingFlowCheckoutNames.idCheckCancelled,
  'id-check-expired': bookingFlowCheckoutNames.idCheckExpired,
  'id-check-not-allowed': bookingFlowCheckoutNames.idCheckNotAllowed,
  'e-signature': bookingFlowCheckoutNames.eSignature,
  'e-signature-expired': bookingFlowCheckoutNames.eSignatureExpired,
  'e-signature-cancelled': bookingFlowCheckoutNames.eSignatureCancelled,
  'create-booking': bookingFlowCheckoutNames.createBooking,
  unknown: bookingFlowCheckoutNames.continueCheckout,
};

const commonRouteMeta = {
  middleware: [guardNames.requiresLanguage],
};

const checkoutPrefix = 'checkout';

function updateHashAndContinue(to: RouteLocationGeneric) {
  const { set } = useCheckoutHash();
  const locationId = parseToInteger(to.params.locationId, undefined) as number;
  const hash = to.params.hash as string;
  set(locationId, hash);
  return bookingFlowCheckoutRouteFactory.continueCheckout();
}

export const bookingFlowCheckoutRoutes: IAppRoutes = [
  // ### Keep these pages outside the orchestrator
  {
    name: bookingFlowCheckoutNames.startCheckout,
    path: `${checkoutPrefix}/start/:hash`,
    redirect: updateHashAndContinue,
  },
  {
    name: bookingFlowCheckoutNames.eSignatureSignedWebhook,
    // 🚨 Keep the path as it is. The BE will redirect here after signing an envelope
    path: 'esignature-signed',
    component: () =>
      import('@booking-portal/modules/booking-flow-checkout/pages/steps/e-signature/BFCESignatureSignedWebhookStep.vue'),
    meta: {
      ...commonRouteMeta,
    },
  },
  {
    name: bookingFlowCheckoutNames.eSignatureError,
    path: `${checkoutPrefix}/esignature-error`,
    component: () =>
      import('@booking-portal/modules/booking-flow-checkout/pages/steps/e-signature/BFCESignatureErrorStep.vue'),
    meta: {
      ...commonRouteMeta,
    },
  },
  {
    name: bookingFlowCheckoutNames.idCheckSetupError,
    path: `${checkoutPrefix}/id-check-setup-error`,
    component: () =>
      import('@booking-portal/modules/booking-flow-checkout/pages/steps/id-check/BFCIDCheckSetupErrorStep.vue'),
    meta: {
      ...commonRouteMeta,
    },
  },
  {
    name: bookingFlowCheckoutNames.success,
    path: `${checkoutPrefix}/success`,
    component: () =>
      import('@booking-portal/modules/booking-flow-checkout/pages/steps/BFCSuccessStep.vue'),
    meta: {
      ...commonRouteMeta,
    },
  },
  {
    name: bookingFlowCheckoutNames.sessionEnded,
    path: `${checkoutPrefix}/session-ended`,
    component: () =>
      import('@booking-portal/modules/booking-flow-checkout/pages/steps/BFCSessionEndedStep.vue'),
    meta: {
      ...commonRouteMeta,
    },
  },
  // Webhook url. Do not change!
  {
    name: bookingFlowCheckoutNames.idCheckWebhook,
    // Keep the path as it is because it is defined by the BE
    path: `${checkoutPrefix}/id-check/:hash`,
    redirect(to: RouteLocationGeneric) {
      const { set } = useCheckoutHash();
      const locationId = parseToInteger(
        to.params.locationId,
        undefined
      ) as number;
      const hash = to.params.hash as string;
      set(locationId, hash);
      // Redirects to the idCheckInProgress page which is a child of the orchestrator.
      // That means that it will check the stored hash and the step and will redirect accordingly
      return bookingFlowCheckoutRouteFactory.idCheckInProgress(locationId);
    },
    meta: {
      ...commonRouteMeta,
      allowedCheckoutSteps: ['id-check'],
    },
  },
  {
    name: bookingFlowCheckoutNames.esignatureWebhook,
    // Keep the path as it is because it is defined by the BE
    path: `${checkoutPrefix}/esignature/:hash`,
    redirect(to: RouteLocationGeneric) {
      const { set } = useCheckoutHash();
      const locationId = parseToInteger(
        to.params.locationId,
        undefined
      ) as number;
      const hash = to.params.hash as string;
      set(locationId, hash);
      // Redirects to the eSignatureVerify page which is a child of the orchestrator.
      // That means that it will check the stored hash and the step and will redirect accordingly
      return bookingFlowCheckoutRouteFactory.eSignatureVerify(locationId);
    },
    meta: {
      ...commonRouteMeta,
      allowedCheckoutSteps: ['e-signature'],
    },
  },
  // ### END - outside orchestrator

  {
    path: `${checkoutPrefix}`,
    name: bookingFlowCheckoutNames.continueCheckout,
    component: () =>
      import('@booking-portal/modules/booking-flow-checkout/pages/BFCOrchestrator.vue'),
    children: [
      {
        name: bookingFlowCheckoutNames.payment,
        path: 'step/payment',
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/payment/BFCPaymentStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['payment'],
        },
      },
      {
        name: bookingFlowCheckoutNames.paymentVerify,
        path: 'step/payment-verify',
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/payment/BFCPaymentVerifyStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['payment'],
        },
      },
      {
        name: bookingFlowCheckoutNames.createBooking,
        path: 'step/create-booking',
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/BFCCreateBookingStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['create-booking'],
        },
      },
      {
        name: bookingFlowCheckoutNames.idCheck,
        path: 'step/id-check',
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/id-check/BFCIDCheckStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['id-check'],
        },
      },
      {
        name: bookingFlowCheckoutNames.idCheckInProgress,
        path: 'step/id-check-in-progress',
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/id-check/BFCIDCheckInProgressStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['id-check'],
        },
      },
      {
        name: bookingFlowCheckoutNames.idCheckExpired,
        path: 'step/id-check-expired',
        props: {
          type: 'expired',
        },
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/id-check/BFCIDCheckErrorStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['id-check-expired'],
        },
      },
      {
        name: bookingFlowCheckoutNames.idCheckCancelled,
        path: 'step/id-check-cancelled',
        props: {
          type: 'cancelled',
        },
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/id-check/BFCIDCheckErrorStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['id-check-cancelled'],
        },
      },
      {
        name: bookingFlowCheckoutNames.idCheckNotAllowed,
        path: 'step/id-check-not-allowed',
        props: {
          type: 'not-allowed',
        },
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/id-check/BFCIDCheckErrorStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['id-check-not-allowed'],
        },
      },

      {
        name: bookingFlowCheckoutNames.eSignature,
        path: 'step/e-signature',
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/e-signature/BFCESignatureStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['e-signature'],
        },
      },
      {
        name: bookingFlowCheckoutNames.eSignatureVerify,
        path: 'step/e-signature-verify',
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/e-signature/BFCESignatureVerifyStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['e-signature'],
        },
      },
      {
        name: bookingFlowCheckoutNames.eSignatureCancelled,
        path: 'step/e-signature-cancelled',
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/e-signature/BFCESignatureCancelledStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['e-signature-cancelled'],
        },
      },
      {
        name: bookingFlowCheckoutNames.eSignatureExpired,
        path: 'step/e-signature-expired',
        component: () =>
          import('@booking-portal/modules/booking-flow-checkout/pages/steps/e-signature/BFCESignatureExpiredStep.vue'),
        meta: {
          ...commonRouteMeta,
          allowedCheckoutSteps: ['e-signature-expired'],
        },
      },
    ],
  },
];

export const bookingFlowCheckoutRouteFactory = {
  startCheckout(locationId: number, hash: string) {
    return {
      name: bookingFlowCheckoutNames.startCheckout,
      params: {
        locationId: locationId,
        hash: hash,
      },
    };
  },
  continueCheckout() {
    return {
      name: bookingFlowCheckoutNames.continueCheckout,
    };
  },
  eSignatureError(locationId: number) {
    return {
      name: bookingFlowCheckoutNames.eSignatureError,
      params: {
        locationId: locationId,
      },
    };
  },
  payment(locationId: number) {
    return {
      name: bookingFlowCheckoutNames.payment,
      params: {
        locationId: locationId,
      },
    };
  },
  paymentVerify(locationId: number) {
    return {
      name: bookingFlowCheckoutNames.paymentVerify,
      params: {
        locationId: locationId,
      },
    };
  },
  idCheck(locationId: number) {
    return {
      name: bookingFlowCheckoutNames.idCheck,
      params: {
        locationId: locationId,
      },
    };
  },
  idCheckInProgress(locationId: number) {
    return {
      name: bookingFlowCheckoutNames.idCheckInProgress,
      params: {
        locationId: locationId,
      },
    };
  },
  idCheckSetupError(locationId: number) {
    return {
      name: bookingFlowCheckoutNames.idCheckSetupError,
      params: {
        locationId: locationId,
      },
    };
  },
  eSignatureVerify(locationId: number) {
    return {
      name: bookingFlowCheckoutNames.eSignatureVerify,
      params: {
        locationId: locationId,
      },
    };
  },
  success(locationId: number) {
    return {
      name: bookingFlowCheckoutNames.success,
      params: {
        locationId: locationId,
      },
    };
  },
  sessionEnded(locationId: number, reason?: ISessionEndedReason) {
    return {
      name: bookingFlowCheckoutNames.sessionEnded,
      params: {
        locationId: locationId,
      },
      query: {
        reason,
      },
    };
  },
  goToBookingFlow() {
    return routesFactoryBookingFlow.continueFlow();
  },
};
